import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  TextField,
  Select,
  Button,
  SelectSection,
  MenuSubheader,
  MenuItem,
} from '@flatfy/supernova-new';
import { FlatsFiltersContentContext } from '../FlatsFiltersContext';
import { attachGaDataAttributes } from '../../helpers/analytics';
import useMediaQuery from '../useMediaQuery';

const HOUSE = 'house_id';
const SECTION = 'section_id';
const CLASS_NO_SCROLL = 'UIPopup-no-scroll';
const EVENT = { category: window.eventCategory, action: 'filters', label: 'select_section_click' };

const FlatsFiltersSelect = ({
  filterParams: { readyState },
  selectRoot,
  onOpenedChange = () => {},
}) => {
  const intl = useIntl();
  const { searchParams, updateSearchParams, flatsCount } = useContext(FlatsFiltersContentContext);
  const [opened, setOpened] = useState(false);
  const isMobile = useMediaQuery();
  const checkedOptionContent = useCallback(() => {
    const content = [];
    readyState.forEach(({ title, options }) => {
      const readyStateContent = options
        .filter((option) => option.active)
        .map((option) => option.title);

      if (readyStateContent.length > 0) {
        readyStateContent.push(title);
        content.push(readyStateContent.join(', '));
      }
    });

    return content.length > 0 ? content.join('; ') : intl.formatMessage({ id: 'all' });
  }, [readyState]);
  const [checkedOption, setCheckedOption] = useState({
    [HOUSE]: searchParams[HOUSE],
    [SECTION]: searchParams[SECTION],
  });
  /* needed for correct selection on pages with both house_id and section_id in params, but with only one type of options in select */
  const availableOptions = useMemo(() => {
    return readyState.reduce(
      (acc, { options }) => [
        ...acc,
        ...options.map((option) => `${option.param_name}|${option.value}`),
      ],
      [],
    );
  }, [readyState]);
  const selectOptions = readyState.map(({ title, value, disabled, active, options }) => {
    const selectedSection = options.every(
      (option) =>
        option.active || option.disabled || checkedOption[option.param_name].includes(option.value),
    );

    return {
      title,
      disabled,
      selected: (active || selectedSection) && !disabled,
      key: value,
      event: { category: window.eventCategory, action: 'filters', label: `ready_state_${value}` },
      options: options.map((option) => ({
        event: {
          category: window.eventCategory,
          action: 'filters',
          label: `${option.param_name === HOUSE ? 'house' : 'section'}_${option.value}`,
        },
        selected: option.active || checkedOption[option.param_name].includes(option.value),
        content: option.title,
        disabled: option.disabled,
        value: `${option.param_name}|${option.value}`,
      })),
    };
  });
  const body = document.querySelector('body');
  const selectWithOneOption = selectOptions.length === 1 && selectOptions[0].options.length < 2;

  const handleChange = (options) => {
    const nextValues = options.reduce(
      (acc, o) => {
        const [key, value] = o.split('|');
        let checkedSections = {};

        if (key === HOUSE && window.params.houses) {
          const sections = window.params.houses[value];

          checkedSections = {
            [SECTION]: [...acc[SECTION], ...sections],
          };
        }

        return {
          ...acc,
          [key]: [...acc[key], Number(value)],
          ...checkedSections,
        };
      },
      {
        [HOUSE]: [],
        [SECTION]: [],
      },
    );

    updateSearchParams({
      ...searchParams,
      ...nextValues,
    });
  };

  const handleResetClick = () => {
    updateSearchParams({
      ...searchParams,
      [HOUSE]: [],
      [SECTION]: [],
    });
  };

  useEffect(() => {
    setCheckedOption({
      [HOUSE]: searchParams[HOUSE],
      [SECTION]: searchParams[SECTION],
    });
  }, [searchParams, checkedOptionContent]);

  return (
    <div className="FlatsFiltersSelect">
      {selectWithOneOption ? (
        <TextField
          name="ready_state"
          label={intl.formatMessage({ id: 'selectLabel' })}
          value={readyState[0].title}
          variant="outline"
          InputProps={{
            readOnly: true,
            ...attachGaDataAttributes(EVENT),
          }}
        />
      ) : (
        <Select
          opened={opened}
          label={intl.formatMessage({ id: 'selectLabel' })}
          onChange={handleChange}
          value={[
            ...checkedOption[HOUSE].map((option) => `${HOUSE}|${option}`),
            ...checkedOption[SECTION].map((option) => `${SECTION}|${option}`),
          ].filter((option) => availableOptions.includes(option))}
          formatValue={checkedOptionContent}
          onOpenedChange={(isOpened) => {
            if (isOpened) {
              body.classList.add(CLASS_NO_SCROLL);
            } else {
              body.classList.remove(CLASS_NO_SCROLL);
            }
            onOpenedChange(isOpened);
            if (opened !== isOpened) {
              setOpened(isOpened);
            }
          }}
          closeOnChange={false}
          actions={
            <div className="FlatsFiltersActions">
              <Button
                variant="secondary-outline"
                onClick={handleResetClick}
                fullWidth
                {...attachGaDataAttributes({
                  category: window.eventCategory,
                  action: 'filters',
                  label: 'select_section_reset',
                })}
              >
                <FormattedMessage id="reset" />
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  body.classList.remove('UIPopup-no-scroll');
                  onOpenedChange(false);
                  setOpened(false);
                }}
                fullWidth
                {...attachGaDataAttributes({
                  category: window.eventCategory,
                  action: 'filters',
                  label: 'select_section_show',
                })}
              >
                {isMobile ? (
                  <FormattedMessage id="select" />
                ) : (
                  <FormattedMessage id="resultsCount" values={{ count: flatsCount }} />
                )}
              </Button>
            </div>
          }
          InputProps={attachGaDataAttributes(EVENT)}
          MenuProps={{
            className: 'FlatsFiltersSelect-content',
            classes: {
              root: 'FlatsFiltersSelect-popup',
            },
            fullWidth: true,
            limitHeight: true,
            ...(selectRoot && !isMobile && { wrapperEl: selectRoot }),
          }}
          multiple
        >
          {selectOptions.map((selectOption) => (
            <SelectSection key={selectOption.key} className="FlatsFiltersSelect-section">
              <MenuSubheader className="FlatsFiltersSelect-subheader">
                <MenuItem
                  selected={selectOption.selected}
                  withCheckbox
                  bold
                  {...attachGaDataAttributes(selectOption.event)}
                >
                  {selectOption.title}
                </MenuItem>
              </MenuSubheader>
              {selectOption.options.map((option) => (
                <MenuItem
                  className="FlatsFiltersSelect-item"
                  key={option.value}
                  value={option.value}
                  selected={option.selected}
                  withCheckbox
                  {...attachGaDataAttributes(option.event)}
                >
                  {option.content}
                </MenuItem>
              ))}
            </SelectSection>
          ))}
        </Select>
      )}
    </div>
  );
};

FlatsFiltersSelect.propTypes = {
  filterParams: PropTypes.object.isRequired,
  selectRoot: PropTypes.object,
  onOpenedChange: PropTypes.func,
};

export default FlatsFiltersSelect;
